'use client';

import { Button, Text } from '@aurora-components';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import {
  CardPaymentSectionWrapper,
  PaymentCardTitleWrapper,
  PaymentInput,
  PaymentInputSection,
  PaymentInputsWrapper,
  PaymentSectionOptionsWrapper,
  PaymentSelectInput,
} from './payment-section.styles';

const MIN_AMOUNT = 500;

export function CardPaymentForm({
  title,
  description,
  options,
  inputLabel,
  buttonLabel,
}: {
  title?: string | null;
  description?: string | null;
  options?: Array<number | null> | null;
  inputLabel?: string | null;
  buttonLabel?: string | null;
}) {
  const { locale } = useParams() as { locale: string };
  const numericOptions = options?.filter((option): option is number => option !== null) ?? [];
  const [selectedAmount, setSelectedAmount] = useState<number | null>(numericOptions[0] ?? null);
  const [customAmount, setCustomAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const amount = customAmount ? Number(customAmount) : selectedAmount;

  async function handleDonate() {
    if (!amount || amount < MIN_AMOUNT || submitting) return;

    setSubmitting(true);
    setError(false);

    try {
      const response = await fetch('/api/donation/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, locale }),
      });

      if (!response.ok) throw new Error('checkout failed');

      const { redirectUrl } = await response.json();
      window.location.assign(redirectUrl);
    } catch {
      setError(true);
      setSubmitting(false);
    }
  }

  return (
    <CardPaymentSectionWrapper>
      <PaymentCardTitleWrapper>
        <Text $variant="h5" $color="primary-base100" $noBalancer $truncateVertical>
          {title}
        </Text>
        <Text $variant="p" $color="primary-base100" $noBalancer $truncateVertical>
          {description}
        </Text>
      </PaymentCardTitleWrapper>
      <PaymentInputsWrapper>
        <PaymentSectionOptionsWrapper>
          {numericOptions.map((option, index) => (
            <div key={index}>
              <PaymentSelectInput
                type="radio"
                id={`option-${index}`}
                name="card-payment-option"
                value={option}
                checked={!customAmount && selectedAmount === option}
                onChange={() => {
                  setSelectedAmount(option);
                  setCustomAmount('');
                }}
              />
              <PaymentInputSection $isSelected={!customAmount && selectedAmount === option} htmlFor={`option-${index}`}>
                {option} {locale === 'is' ? 'Kr.' : 'ISK'}
              </PaymentInputSection>
            </div>
          ))}
        </PaymentSectionOptionsWrapper>
        <PaymentInput
          type="number"
          step={1}
          min={MIN_AMOUNT}
          placeholder={inputLabel ?? ''}
          aria-label={inputLabel ?? 'Custom donation amount'}
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          inputMode="numeric"
          pattern="[0-9]*"
        />
        <Button onClick={handleDonate} disabled={!amount || amount < MIN_AMOUNT || submitting}>
          {buttonLabel}
        </Button>
        {error && (
          <Text $variant="p" $color="primary-base100" $noBalancer $truncateVertical>
            {locale === 'en' ? 'Something went wrong, please try again.' : 'Villa kom upp, vinsamlegast reyndu aftur.'}
          </Text>
        )}
      </PaymentInputsWrapper>
    </CardPaymentSectionWrapper>
  );
}
